import * as THREE from 'three';

import type { RichSatellite } from '../data/types';
import { ecefToScene } from '../lib/geo3d';

const EARTH_RADIUS = 1;
const PICK_RADIUS_MIN = 20;
const PICK_RADIUS_MAX = 36;

const scratchVec = new THREE.Vector3();
const scratchCamDir = new THREE.Vector3();
const scratchToSat = new THREE.Vector3();

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function pickRadiusPx(viewDistance: number): number {
  if (viewDistance <= 8) {
    return THREE.MathUtils.lerp(PICK_RADIUS_MAX, PICK_RADIUS_MIN, smoothstep(1.2, 8, viewDistance));
  }
  return THREE.MathUtils.lerp(PICK_RADIUS_MIN, 44, smoothstep(8, 64, viewDistance));
}

function projectToScreen(
  world: THREE.Vector3,
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): { x: number; y: number; visible: boolean } {
  scratchVec.copy(world).project(camera);
  if (scratchVec.z < -1 || scratchVec.z > 1) return { x: 0, y: 0, visible: false };
  return {
    x: ((scratchVec.x + 1) / 2) * width,
    y: ((1 - scratchVec.y) / 2) * height,
    visible: true,
  };
}

/**
 * True when the Earth sphere blocks the segment from the camera to the satellite.
 * Uses camera-position ray-sphere math (reference rw) plus entry/exit so LEO sats
 * past Earth's limb are not treated as occluded.
 */
function isEarthOccluded(camera: THREE.PerspectiveCamera, satWorld: THREE.Vector3): boolean {
  const camPos = camera.position;

  // Far-side hemisphere relative to Earth center (reference aw).
  if (satWorld.dot(camPos) < 0) return true;

  camera.getWorldDirection(scratchCamDir);
  scratchToSat.subVectors(satWorld, camPos);
  if (scratchToSat.dot(scratchCamDir) <= 0) return true;

  const distToSat = scratchToSat.length();
  if (distToSat < 1e-6) return false;

  scratchToSat.multiplyScalar(1 / distToSat);

  const b = 2 * camPos.dot(scratchToSat);
  const c = camPos.lengthSq() - EARTH_RADIUS * EARTH_RADIUS;
  const disc = b * b - 4 * c;
  if (disc < 0) return false;

  const sqrtDisc = Math.sqrt(disc);
  const tEnter = (-b - sqrtDisc) * 0.5;
  const tExit = (-b + sqrtDisc) * 0.5;

  if (tEnter > distToSat - 0.008) return false;
  if (tExit < 0.008) return false;
  if (distToSat > tExit + 0.008) return false;

  return true;
}

function isSatPickable(camera: THREE.PerspectiveCamera, satWorld: THREE.Vector3): boolean {
  return !isEarthOccluded(camera, satWorld);
}

export function pickSatelliteAtScreen(
  satellites: RichSatellite[],
  selectedNoradId: string | null,
  camera: THREE.PerspectiveCamera,
  screenX: number,
  screenY: number,
  width: number,
  height: number,
  viewDistance = camera.position.length(),
): string | null {
  const pickRadius = pickRadiusPx(viewDistance);
  const camPos = camera.position;
  let best: { id: string; screenDist: number; camDist: number } | null = null;

  for (const sat of satellites) {
    const [x, y, z] = ecefToScene(sat.position.ecef);
    scratchVec.set(x, y, z);
    if (!isSatPickable(camera, scratchVec)) continue;

    const projected = projectToScreen(scratchVec, camera, width, height);
    if (!projected.visible) continue;

    const screenDist = Math.hypot(projected.x - screenX, projected.y - screenY);
    if (screenDist > pickRadius) continue;

    const camDist = camPos.distanceToSquared(scratchVec);
    if (!best) {
      best = { id: sat.noradId, screenDist, camDist };
      continue;
    }
    if (screenDist < best.screenDist - 2) {
      best = { id: sat.noradId, screenDist, camDist };
    } else if (screenDist <= best.screenDist + 2 && camDist < best.camDist) {
      best = { id: sat.noradId, screenDist, camDist };
    }
  }

  return best?.id ?? null;
}

export function hoverSatelliteAtScreen(
  satellites: RichSatellite[],
  selectedNoradId: string | null,
  camera: THREE.PerspectiveCamera,
  screenX: number,
  screenY: number,
  width: number,
  height: number,
  viewDistance = camera.position.length(),
): boolean {
  return (
    pickSatelliteAtScreen(
      satellites,
      selectedNoradId,
      camera,
      screenX,
      screenY,
      width,
      height,
      viewDistance,
    ) != null
  );
}
